// Browser -> ESP commands and their answers. Formats: firmware/main/cmd_logic.h
// (frames), firmware/main/stream.h (the FE 08 response event) and
// firmware/main/config_logic.h (the configuration).

export const CMD = {
  GET_INFO: 1, GET_CONFIG: 2, SET_CONFIG: 3, ERASE_CONFIG: 4, REBOOT: 5, PIN_TEST: 6, PIN_COUNTS: 7, PIN_PULSE: 8,
  WIFI_SET: 0x0a, WIFI_MODE: 0x0b, WIFI_SCAN: 0x0c, SET_SERVER: 0x0d, SET_KEY: 0x0e, GET_KEY: 0x0f, SET_NETMODE: 0x10, NET_STATUS: 0x11, NET_USB: 0x13,
  UB_CONFIG: 0x14, UB_OPEN: 0x15, UB_STATUS: 0x16, UB_BODY: 0x17, UB_REQUEST: 0x18, UB_CLOSE: 0x19, OPEN: 0x1c, ACK: 0x1d,
};
// States of the HTTP bridge to an Ultimate (firmware/main/ubridge.h)
export const UB = { CLOSED: 0, CONNECTING: 1, READY: 2, BUSY: 3, DONE: 4, FAILED: 5, GET: 1, PUT: 2, POST: 3 };
// Radio modes (CMD_WIFI_MODE, firmware/main/wifi_radio.h) the page uses: off and scanning.
export const RADIO = { OFF: 0, SCAN: 1 };
export const NET_ERRORS = ['none', 'not configured', 'Wi-Fi did not connect', 'time (SNTP) not reachable', 'server not reachable (TLS or network)', 'the server does not know this key', 'server error'];
export const WIFI_AUTH = ['open', 'WEP', 'WPA', 'WPA2', 'WPA/WPA2', 'WPA2 enterprise', 'WPA3', 'WPA2/WPA3'];
// Pin test modes (firmware/main/pintest_logic.h): STOP restarts the ESP; WATCH
// reports debounced presses of the masked pins (inputs with pull-up) as stream
// events, and is the only mode that may include GPIO0 (the BOOT button). EDGES counts
// every edge of the masked pins (inputs, pull-up on); read them with pinCounts().
export const PIN_MODE = { STOP: 0, BLINK: 1, HIGH: 2, WATCH: 3, EDGES: 4 };
export const STATUS = { OK: 0, ERR_ARGS: 1, ERR_UNKNOWN: 2, ERR_STORAGE: 3 };
export const CHIP_ESP32 = 1;
export const PROTOCOL_VERSION = 6; // the firmware's version.h
export const NC = 0xff; // pin not connected

// CRC-8, polynomial 0x07, initial value 0 (same as the firmware).
export function crc8(bytes) {
  let crc = 0;
  for (const b of bytes) {
    crc ^= b;
    for (let i = 0; i < 8; i++) crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
  }
  return crc;
}

// A5 5A len cmd payload... crc   (len = 1 + payload size, crc over len, cmd, payload)
export function buildFrame(cmd, payload = []) {
  const body = [1 + payload.length, cmd, ...payload];
  return Uint8Array.from([0xa5, 0x5a, ...body, crc8(body)]);
}

// Response bytes (from StreamParser.onResponse): [cmd | 0x80, status, payload...]
export function parseResponse(bytes) {
  if (bytes.length < 2) return null;
  return { cmd: bytes[0] & 0x7f, status: bytes[1], payload: bytes.subarray(2) };
}

// The ESP32 (DevKit V1) pins that may be probed or assigned: the GPIOs that can drive an output and have no job at
// boot (GPIO4, 13, 14, 16-19, 21-23, 25-27, 32, 33). Everything else is off limits (UART0, flash, strapping,
// input only), see cfg_pin_is_candidate() in firmware/main/config_logic.h, which this mirrors.
export const CANDIDATE_PINS = [4, 13, 14, 16, 17, 18, 19, 21, 22, 23, 25, 26, 27, 32, 33];
// The LED may also be on GPIO2, the DevKit's onboard LED (blink and steady-high pin tests only; see cfg_led_pin_ok()).
export const LED_PINS = [2, ...CANDIDATE_PINS];
export const isCandidatePin = (p) => CANDIDATE_PINS.includes(p);

// Bit n = GPIO n, 6 bytes (GPIO0..47), as the PIN_TEST command expects.
export function pinMask(pins) {
  const mask = new Uint8Array(6);
  for (const p of pins) mask[p >> 3] |= 1 << (p & 7);
  return mask;
}

// There are no default pins: an unconfigured device does nothing on the bus until
// the Button, LED and Bus pins wizards have run (each saves its part and sets its
// "done" flag; "none" is a valid answer for the button and the LED).
export const EMPTY_CONFIG = Object.freeze({
  dat: null, clk: null, atn: null, rst: null, led: null, button: null, led2: null,
  ledActiveLow: false, led2ActiveLow: false, buttonDone: false, ledDone: false,
});

// led = the first LED (the print LED, which also shows the Wi-Fi states when there is no second one), led2 = the
// second LED (the Wi-Fi LED), only with a first one.
const FIELDS = ['dat', 'clk', 'atn', 'rst', 'led', 'button', 'led2'];
const FLAG_LED_ACTIVE_LOW = 1;
const FLAG_BUTTON_DONE = 2;
const FLAG_LED_DONE = 4;
const FLAG_LED2_ACTIVE_LOW = 8;
export const CONFIG_BYTES = 8;

// Config object (pins as numbers or null for "not connected") <-> 8 bytes: dat clk atn rst led button, the flags
// byte, then the second LED (the order of firmware/main/config_logic.h cfg_encode).
export function encodeConfig(c) {
  const flags = (c.ledActiveLow ? FLAG_LED_ACTIVE_LOW : 0) | (c.buttonDone ? FLAG_BUTTON_DONE : 0) | (c.ledDone ? FLAG_LED_DONE : 0) |
    (c.led2ActiveLow ? FLAG_LED2_ACTIVE_LOW : 0);
  const pin = (f) => (c[f] == null ? NC : c[f]);
  return Uint8Array.from([pin('dat'), pin('clk'), pin('atn'), pin('rst'), pin('led'), pin('button'), flags, pin('led2')]);
}

export function decodeConfig(bytes) {
  const c = {};
  ['dat', 'clk', 'atn', 'rst', 'led', 'button'].forEach((f, i) => (c[f] = bytes[i] === NC ? null : bytes[i]));
  c.led2 = bytes[7] === NC ? null : bytes[7];
  c.ledActiveLow = (bytes[6] & FLAG_LED_ACTIVE_LOW) !== 0;
  c.led2ActiveLow = (bytes[6] & FLAG_LED2_ACTIVE_LOW) !== 0;
  c.buttonDone = (bytes[6] & FLAG_BUTTON_DONE) !== 0;
  c.ledDone = (bytes[6] & FLAG_LED_DONE) !== 0;
  return c;
}

// What is still to do: {bus, button, led, ready}, each true once done. Same rule as
// cfg_ready() in the firmware: only a device with all three done listens on the bus.
export function setupState(c) {
  const bus = c.dat != null && c.clk != null && c.atn != null;
  return { bus, button: !!c.buttonDone, led: !!c.ledDone, ready: bus && !!c.buttonDone && !!c.ledDone };
}

// Same rules as cfg_valid() in the firmware, so the UI can refuse early. The button
// may also be on GPIO0 (the board's BOOT button); no other field may. The bus lines
// are all set or all unset (a partly set up device is fine).
export function validateConfig(c) {
  const used = [];
  const bus = ['dat', 'clk', 'atn'].filter((f) => c[f] != null).length;
  if (bus !== 0 && bus !== 3) return 'dat, clk and atn go together';
  if (c.led2 != null && c.led == null) return 'a second LED needs a first one';
  for (const f of FIELDS) {
    const p = c[f];
    if (p == null) continue;
    if (!isCandidatePin(p) && !(f === 'button' && p === 0) && !((f === 'led' || f === 'led2') && p === 2)) return `GPIO${p} cannot be used (${f})`;
    if (used.includes(p)) return `GPIO${p} is used twice`;
    used.push(p);
  }
  return null;
}

// CMD_NET_STATUS answer (firmware/main/cmd.c)
export function decodeNetStatus(p) {
  if (p.length < 17) return null;
  const u16 = (o) => (p[o] << 8) | p[o + 1];
  const u32 = (o) => ((p[o] << 24) | (p[o + 1] << 16) | (p[o + 2] << 8) | p[o + 3]) >>> 0;
  return {
    wifiMode: (p[0] & 1) !== 0,
    hasCredentials: (p[0] & 2) !== 0,
    hasServer: (p[0] & 4) !== 0,
    hasKey: (p[0] & 8) !== 0,
    ready: (p[0] & 16) !== 0,
    clockSet: (p[0] & 32) !== 0,
    usbMode: (p[0] & 64) !== 0, // the browser asked for the USB stream (CMD_NET_USB); not stored on the device
    wifiState: p[1], // 0 off, 1 on but not connected, 2 online
    lastHelloHttp: u16(2),
    lastPushHttp: u16(4),
    chunksAccepted: u32(6),
    failedSessions: u16(10),
    error: p[12],
    errorText: NET_ERRORS[p[12]] ?? `error ${p[12]}`,
    buffered: u32(13),
  };
}

export function describeInfo(payload) {
  if (payload.length < 6) return null;
  return {
    firmware: `${payload[0]}.${payload[1]}.${payload[2]}`,
    protocol: payload[3],
    chip: payload[4],
    ready: payload[5] === 1, // all setup wizards done: the device listens on the bus
  };
}

// Sends commands through `write(Uint8Array)` and matches the responses that the
// stream parser delivers to handleResponse().
export class DeviceLink {
  constructor(write, { timeoutMs = 3000 } = {}) {
    this.write = write;
    this.timeoutMs = timeoutMs;
    this.pending = new Map(); // cmd -> {resolve, timer}
  }

  handleResponse(bytes) {
    const r = parseResponse(bytes);
    const p = r && this.pending.get(r.cmd);
    if (!p) return;
    this.pending.delete(r.cmd);
    clearTimeout(p.timer);
    p.resolve(r);
  }

  // Resolves with {cmd, status, payload}; rejects if no answer arrives in time.
  send(cmd, payload = []) {
    return new Promise((resolve, reject) => {
      this.pending.get(cmd)?.reject?.(new Error('superseded'));
      const timer = setTimeout(() => {
        this.pending.delete(cmd);
        reject(new Error(`no answer to device command ${cmd}`));
      }, this.timeoutMs);
      this.pending.set(cmd, { resolve, reject, timer });
      Promise.resolve(this.write(buildFrame(cmd, payload))).catch((e) => {
        clearTimeout(timer);
        this.pending.delete(cmd);
        reject(e);
      });
    });
  }

  async getInfo() {
    const r = await this.send(CMD.GET_INFO);
    return r.status === STATUS.OK ? describeInfo(r.payload) : null;
  }

  async getConfig() {
    const r = await this.send(CMD.GET_CONFIG);
    if (r.status !== STATUS.OK || r.payload.length < 8) return null;
    return { stored: r.payload[0] === 1, config: decodeConfig(r.payload.subarray(1)) };
  }

  // The ESP restarts after a successful SET_CONFIG / ERASE_CONFIG / REBOOT, so the
  // serial port goes away and has to be opened again.
  async setConfig(config) {
    const problem = validateConfig(config);
    if (problem) throw new Error(problem);
    return (await this.send(CMD.SET_CONFIG, [...encodeConfig(config)])).status;
  }

  // Drives `pins` as a pin test (blink or steady high); every other candidate pin
  // is floating. STOP ends the test and restarts the ESP.
  async pinTest(mode, pins = []) {
    const bad = pins.find((p) => !isCandidatePin(p) && !(mode === PIN_MODE.WATCH && p === 0) && !((mode === PIN_MODE.BLINK || mode === PIN_MODE.HIGH) && p === 2));
    if (bad !== undefined) throw new Error(`GPIO${bad} cannot be tested`);
    return (await this.send(CMD.PIN_TEST, [mode, ...pinMask(pins)])).status;
  }

  // EDGES test: edge counts per pin since the last call, e.g. {16: 20, 33: 4}.
  async pinCounts() {
    const r = await this.send(CMD.PIN_COUNTS);
    if (r.status !== STATUS.OK) throw new Error(`the device cannot report edge counts (status ${r.status})`);
    const p = r.payload;
    const counts = {};
    for (let i = 1; i + 2 < p.length; i += 3) counts[p[i]] = p[i + 1] | (p[i + 2] << 8);
    return { counts, truncated: (p[0] & 1) === 1 };
  }

  // EDGES test: pull `pin` low for ms (10 ms steps, up to 500), then release it.
  async pinPulse(pin, ms = 150) {
    if (!isCandidatePin(pin)) throw new Error(`GPIO${pin} cannot be pulsed`);
    const units = Math.min(50, Math.max(1, Math.round(ms / 10)));
    return (await this.send(CMD.PIN_PULSE, [pin, units])).status;
  }

  // ---- Wi-Fi printing (firmware/main/net.c); SET_SERVER, SET_KEY and SET_NETMODE restart the device ----

  // Stores the credentials (the password is never read back). An empty password is an open network.
  async setWifi(ssid, password = '') {
    const s = new TextEncoder().encode(ssid);
    const p = new TextEncoder().encode(password);
    if (s.length < 1 || s.length > 32) throw new Error('the network name is 1 to 32 bytes');
    if (p.length > 63 || (p.length > 0 && p.length < 8)) throw new Error('the password is 8 to 63 characters (or empty for an open network)');
    return (await this.send(CMD.WIFI_SET, [s.length, ...s, p.length, ...p])).status;
  }

  // The networks the device can see: [{ssid, rssi, channel, auth}], strongest first, one entry per name.
  // The radio scans for a few seconds.
  async scanWifi({ waitMs = 7000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
    if ((await this.send(CMD.WIFI_MODE, [RADIO.SCAN])).status !== STATUS.OK) throw new Error('the device cannot scan');
    try {
      await sleep(waitMs);
      const seen = new Map();
      for (let i = 0; i < 20; i++) {
        const r = await this.send(CMD.WIFI_SCAN, [i]);
        if (r.status !== STATUS.OK || r.payload.length < 36) break;
        const p = r.payload;
        let end = 4;
        while (end < 36 && p[end] !== 0) end++;
        const ssid = new TextDecoder().decode(p.subarray(4, end));
        const rssi = (p[1] << 24) >> 24;
        const prev = seen.get(ssid);
        if (!prev || rssi > prev.rssi) seen.set(ssid, { ssid, rssi, channel: p[2], auth: p[3] });
      }
      return [...seen.values()].sort((a, b) => b.rssi - a.rssi);
    } finally {
      await this.send(CMD.WIFI_MODE, [RADIO.OFF]).catch(() => {});
    }
  }

  async setServer(url) {
    if (!/^https?:\/\/[^/\s]+/.test(url) || url.length > 96) throw new Error('the server address is http:// or https:// and at most 96 characters');
    return (await this.send(CMD.SET_SERVER, [...new TextEncoder().encode(url.replace(/\/+$/, ''))])).status;
  }

  async setKey(key) {
    if (key.length !== 32) throw new Error('a key is 32 bytes');
    return (await this.send(CMD.SET_KEY, [...key])).status;
  }

  // The key stored in the device (null if there is none): physical access to the device is the trust model.
  async getKey() {
    const r = await this.send(CMD.GET_KEY);
    return r.status === STATUS.OK && r.payload.length === 32 ? Uint8Array.from(r.payload) : null;
  }

  // The "Use Wi-Fi printing" checkbox: true = the USB stream for as long as this connection lasts (not stored).
  async setUsbStream(usb) {
    return (await this.send(CMD.NET_USB, [usb ? 1 : 0])).status;
  }

  // ---- the HTTP bridge to an Ultimate (ubridge.c): the ESP makes the requests over its Wi-Fi ----
  async ubConfig(host, password = '') {
    const h = new TextEncoder().encode(host);
    const p = new TextEncoder().encode(password);
    if (h.length === 0 || h.length > 63 || p.length > 47 || 2 + h.length + p.length > 100) throw new Error('the address or the password is too long');
    return (await this.send(CMD.UB_CONFIG, [h.length, ...h, p.length, ...p])).status;
  }

  async ubOpen() {
    return (await this.send(CMD.UB_OPEN)).status;
  }

  async ubClose() {
    return (await this.send(CMD.UB_CLOSE)).status;
  }

  // {state, wifiState, error, http, body}: body = the first bytes of the last answer as text
  async ubStatus() {
    const r = await this.send(CMD.UB_STATUS);
    if (r.status !== STATUS.OK || r.payload.length < 6) return null;
    const p = r.payload;
    return { state: p[0], wifiState: p[1], error: p[2], http: (p[3] << 8) | p[4], body: new TextDecoder().decode(p.subarray(6, 6 + p[5])) };
  }

  // Uploads the request body in pieces of 96 bytes (statuses are returned in order; 0 = all fine).
  async ubBody(bytes) {
    for (let off = 0; off < bytes.length; off += 96) {
      const piece = bytes.subarray(off, off + 96);
      const status = (await this.send(CMD.UB_BODY, [off >> 8, off & 255, ...piece])).status;
      if (status !== STATUS.OK) return status;
    }
    return STATUS.OK;
  }

  async ubRequest(method, path, bodyLength = 0) {
    return (await this.send(CMD.UB_REQUEST, [method, bodyLength >> 8, bodyLength & 255, ...new TextEncoder().encode(path)])).status;
  }

  async setNetMode(wifi) {
    return (await this.send(CMD.SET_NETMODE, [wifi ? 1 : 0])).status;
  }

  // What the device says about its print server link.
  async netStatus() {
    const r = await this.send(CMD.NET_STATUS);
    if (r.status !== STATUS.OK) return null;
    return decodeNetStatus(r.payload);
  }

  async eraseConfig() {
    return (await this.send(CMD.ERASE_CONFIG)).status;
  }

  async reboot() {
    return (await this.send(CMD.REBOOT)).status;
  }
}
